import { create } from 'zustand';
import { ASSET_INVENTORY_SORT_KEY } from '@repo/contracts';
import { Logger } from '@/lib/app-log.js';

export const ASSET_LIST_DEFAULT_SORT = {
    sortBy: ASSET_INVENTORY_SORT_KEY,
    sortDirection: 'asc',
};

export const useAssetsStore = create((set, get) => ({
    // Data State
    items: [],
    loading: false,
    error: null,
    success: null,

    // UI State
    isFormOpen: false,
    selectedItem: null,
    stats: null,
    relationships: null, // Store graph data if needed globally, or keep local

    // Pagination & Filtering
    pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    },
    sorting: { ...ASSET_LIST_DEFAULT_SORT },
    search: '',
    columnFilters: {},
    uniqueValues: {},
    dropdownData: null,

    // Actions
    setData: (items) => set({ items }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setSuccess: (success) => set({ success }),
    setStats: (stats) => set({ stats }),
    setSelectedItem: (selectedItem) => set({ selectedItem }),

    setPagination: (pagination) => set((state) => ({
        pagination: { ...state.pagination, ...pagination }
    })),

    setSorting: (sorting) => set((state) => ({
        sorting: { ...state.sorting, ...sorting }
    })),

    setSearch: (search) => set({ search }),

    setColumnFilters: (filters) => set({ columnFilters: filters }),
    setUniqueValues: (uniqueValues) => set({ uniqueValues }),
    setDropdownData: (dropdownData) => set({ dropdownData }),

    // Form Helpers
    openForm: (item = null) => set({ isFormOpen: true, selectedItem: item }),
    closeForm: () => set({ isFormOpen: false, selectedItem: null }),

    // Delete Dialog Helpers
    isDeleteDialogOpen: false,
    openDeleteDialog: (item) => set({ isDeleteDialogOpen: true, selectedItem: item }),
    closeDeleteDialog: () => set({ isDeleteDialogOpen: false, selectedItem: null }),

    // Clearers
    clearError: () => set({ error: null }),
    clearSuccess: () => set({ success: null }),
    reset: () => set({
        items: [],
        loading: false,
        error: null,
        success: null,
        isFormOpen: false,
        isDeleteDialogOpen: false,
        selectedItem: null,
        search: '',
        columnFilters: {},
        sorting: { ...ASSET_LIST_DEFAULT_SORT },
        uniqueValues: {},
        stats: null,
        relationships: null,
        currentAsset: null,
        pagination: {
            currentPage: 1,
            itemsPerPage: 10,
            totalItems: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false
        }
    }),

    // Added for AssetDetail
    setCurrentAsset: (currentAsset) => set({ currentAsset }),

    fetchAssetById: async (id) => {
        const prev = get().currentAsset;
        const idStr = String(id);
        const prevId = prev?._id != null ? String(prev._id) : prev?.id != null ? String(prev.id) : null;
        const shouldClearAsset = !prevId || prevId !== idStr;
        set({
            loading: true,
            error: null,
            ...(shouldClearAsset ? { currentAsset: null } : {})
        });
        try {
            // we will import assetsIntegration lazily to avoid circular dependency
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.getAsset(id);
            if (response.success !== false) {
                set({ currentAsset: response.data, selectedItem: response.data });
            } else {
                set({ error: "Failed to fetch asset details.", currentAsset: null });
            }
        } catch (error) {
            Logger.error("fetchAssetById error:", { error: error?.message });
            set({ error: error.message, currentAsset: null });
        } finally {
            set({ loading: false });
        }
    },

    /*
    - [x] Dynamic AssetRiskHeatMap grid driven by tenant matrix configuration (see `/definitions/scale/risk-assessment-matrix`).
    */
    updateAsset: async (data, isDetailUpdate = false) => {
        if (!isDetailUpdate) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const id = data.id || data._id;
            const response = await assetsIntegration.updateAsset(id, data, { skipCloseForm: isDetailUpdate });
            if (response.pendingApproval) return;
            if (response.success !== false && isDetailUpdate) {
                set({ currentAsset: response.data });
            }
        } finally {
            if (!isDetailUpdate) set({ loading: false });
        }
    },

    linkRisksToAsset: async (assetId, riskIds, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.addRisks(assetId, riskIds);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    unlinkRiskFromAsset: async (assetId, riskId, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.removeRisk(assetId, riskId);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    unlinkRisksBulkFromAsset: async (assetId, riskIds, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.removeRisksBulk(assetId, riskIds);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    linkThreatsToAsset: async (assetId, threatIds, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.addThreats(assetId, threatIds);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    unlinkThreatFromAsset: async (assetId, threatId, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.removeThreat(assetId, threatId);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    unlinkThreatsBulkFromAsset: async (assetId, threatIds, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.removeThreatsBulk(assetId, threatIds);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    linkScfControlsToAsset: async (assetId, scfControlIds, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.addScfControls(assetId, scfControlIds);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    unlinkScfControlFromAsset: async (assetId, scfControlId, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.removeScfControl(assetId, scfControlId);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    unlinkScfControlsBulkFromAsset: async (assetId, scfControlIds, opts = {}) => {
        if (!opts.skipGlobalLoading) set({ loading: true });
        try {
            const { assetsIntegration } = await import('@/lib/api/client');
            const response = await assetsIntegration.removeScfControlsBulk(assetId, scfControlIds);
            if (response.pendingApproval || response.draftSaved) return response;
            if (response.success !== false && response.data) {
                set({ currentAsset: response.data });
            }
            return response;
        } finally {
            if (!opts.skipGlobalLoading) set({ loading: false });
        }
    },

    fetchDropdownData: async (opts) => {
        const { assetsIntegration } = await import('@/lib/api/client');
        return assetsIntegration.fetchDropdownData(opts);
    }
}));
